import { useCallback, useMemo } from "react";

import type { ISwSettings } from "app/@types/ISwSettings";
import { swSettings } from "app/common/services/swSettings";
import type {
    ICampaignDiscountBannerConfig,
    ICampaignPromoModalStackConfig,
} from "app/components/discountCampaign/campaignScenarioConfig.types";
import getIsDiscountActive from "app/components/discountCampaign/common/utils/getIsDiscountActive";
import { getPackageHrefWithPromoCode } from "app/components/discountCampaign/DiscountBanner/utils/getPackageHrefWithPromoCode";
import { useDunnings } from "app/components/hooks/useDunnings";
import { useFitScoreContext } from "app/contexts/FitScoreContext/FitScoreContext";
import { getFirstSubscriptionDate, getUserPlan } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";

import type { IDiscountCampaignConfig } from "../types/discountCampaignConfig";

export interface IUseDiscountCampaignApi {
    shouldShowCampaign: boolean;
    bannerConfig: ICampaignDiscountBannerConfig;
    modalConfig: ICampaignPromoModalStackConfig;
    discountEndTime: string;
    onCtaClick: () => void;
}

export const useDiscountCampaign = <T extends string>(config: IDiscountCampaignConfig<T>): IUseDiscountCampaignApi => {
    const { fitScore } = useFitScoreContext();

    // Multi-variant campaigns (AEO) derive the key from the user plan; single-config
    // campaigns (FirstWeek) fall back to the subscription start date. The `""` sentinel
    // only appears when the campaign is ineligible, in which case `discountDateConfig`
    // stays null and the value is never consumed by a getter that cares.
    const campaignKey = (config.getCampaignType?.(getUserPlan(swSettings as ISwSettings)) ??
        getFirstSubscriptionDate(swSettings) ??
        "") as T;

    const discountDateConfig = campaignKey ? config.getDiscountDateConfig(campaignKey) : null;
    const isEligible = config.isEligible(swSettings as ISwSettings, {
        isMarketingGreenLight: !!fitScore?.Var_Marketing_Greenlight,
    });
    const isDiscountActive = discountDateConfig ? getIsDiscountActive(discountDateConfig) : false;
    const { hasDunnings } = useDunnings();

    const shouldShowCampaign = !hasDunnings && isEligible && isDiscountActive;

    const bannerConfig = useMemo(() => config.getBannerConfig(campaignKey), [campaignKey, config]);

    const onCtaClick = useCallback(() => {
        const url = config.overrideCtaUrl
            ? config.overrideCtaUrl(campaignKey)
            : getPackageHrefWithPromoCode(config.getPromoCode(campaignKey), config.annualPromoCodes);
        window.open(url);
    }, [campaignKey, config]);

    const modalConfig = useMemo(() => config.getModalConfig(campaignKey), [campaignKey, config]);
    return {
        shouldShowCampaign,
        bannerConfig,
        discountEndTime: discountDateConfig?.endDate ?? "",
        onCtaClick,
        modalConfig,
    };
};
